import { Link } from "react-router-dom";
import { Users, Fuel, Settings, ShieldCheck, ArrowRight, Plane, Building2, Zap, Shield, Crown, Star } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";

const BADGE_META = {
  airport_delivery: { label: "Aéroport", icon: Plane, color: "bg-sky-100 text-sky-700" },
  hotel_delivery: { label: "Hôtel", icon: Building2, color: "bg-violet-100 text-violet-700" },
  instant_booking: { label: "Instantanée", icon: Zap, color: "bg-amber-100 text-amber-800" },
  insurance_included: { label: "Assurance", icon: Shield, color: "bg-emerald-100 text-emerald-700" },
  premium: { label: "Premium", icon: Crown, color: "bg-[#d4a437]/15 text-[#0a1e3f]" },
};

export default function VehicleCard({ v }) {
  const { t } = useI18n();
  const { fmt } = useCurrency();
  const photo = (v.photos && v.photos[0]) || "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800";
  const badges = (v.badges || []).slice(0, 3);
  return (
    <div className="bg-white rounded-2xl overflow-hidden mbk-card-shadow border border-gray-100 mbk-card-hover" data-testid={`vehicle-card-${v.id}`}>
      <div className="relative h-52 overflow-hidden bg-gray-100">
        <img src={photo} alt={v.name} className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur text-[#0a1e3f] text-xs font-bold px-3 py-1 rounded-full">{v.category}</div>
        {v.agency?.verified && (
          <div className="absolute top-3 right-3 bg-[#dcfce7] text-[#0e7a3f] text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> {t("verified_agency")}
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-lg text-[#0a1e3f]">{v.name}</h3>
        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>{v.agency?.name} · {v.agency?.city}</span>
          {v.agency?.rating > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[#0a1e3f] font-bold">
              <Star className="h-3 w-3 fill-[#d4a437] text-[#d4a437]" />
              {Number(v.agency.rating).toFixed(1)}
            </span>
          )}
        </p>
        {(badges.length > 0 || v.with_driver_available) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {v.with_driver_available && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0a1e3f] text-white inline-flex items-center gap-1">
                <Users className="h-2.5 w-2.5" /> Chauffeur
              </span>
            )}
            {badges.map((b) => {
              const m = BADGE_META[b];
              if (!m) return null;
              const Icon = m.icon;
              return (
                <span key={b} className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${m.color}`}>
                  <Icon className="h-2.5 w-2.5" /> {m.label}
                </span>
              );
            })}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {v.seats} {t("seats")}</span>
          <span className="inline-flex items-center gap-1"><Settings className="h-3.5 w-3.5" /> {v.transmission}</span>
          <span className="inline-flex items-center gap-1"><Fuel className="h-3.5 w-3.5" /> {v.fuel}</span>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-xl font-black text-[#0a1e3f]">{fmt(v.price_per_day)}</div>
            <div className="text-xs text-gray-500 -mt-0.5">{t("per_day")}{v.min_duration_days > 1 ? ` · min ${v.min_duration_days}j` : ""}</div>
          </div>
          <Link to={`/vehicle/${v.id}`} data-testid={`view-vehicle-${v.id}`} className="mbk-btn-primary text-white px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 rounded-full">
            {t("view")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
